import React from 'react';
import type { RenewalFormData } from './renewalFormTypes';
import MultiSelectDropdown from './MultiSelectDropdown';
import './AcademicRenewalForm.css';

type RenewalTexts = ReturnType<
  typeof import('./hooks/useAcademicRenewalFormTexts').useAcademicRenewalFormTexts
>;

export type RenewalFieldAccent = 'pink' | 'teal';

const accentRadio: Record<RenewalFieldAccent, string> = {
  pink: 'text-pink-600 focus:ring-pink-500 border-gray-300',
  teal: 'text-teal-600 focus:ring-teal-500 border-gray-300',
};

const accentCheckbox: Record<RenewalFieldAccent, string> = {
  pink: 'border-slate-300 text-pink-600 focus:ring-pink-500',
  teal: 'border-slate-300 text-teal-600 focus:ring-teal-500',
};

const accentInput: Record<RenewalFieldAccent, string> = {
  pink: 'focus:ring-pink-500 focus:border-pink-500',
  teal: 'focus:ring-teal-500 focus:border-teal-500',
};

interface AcademicRenewalFormFieldsProps {
  mode: 'full' | 'step';
  stepIndex?: number;
  formData: RenewalFormData;
  errors: Record<string, string>;
  t: RenewalTexts;
  accent?: RenewalFieldAccent;
  handleInputChange: (field: keyof RenewalFormData, value: string | number) => void;
  setMultiSelect: (field: 'usageFrequency' | 'access', value: string[]) => void;
}

const multiTheme = (accent: RenewalFieldAccent): 'pink' | 'orange' | 'teal' =>
  accent === 'teal' ? 'teal' : 'pink';

const optionRowClass = (accent: RenewalFieldAccent, selected: boolean) => {
  const base = 'flex cursor-pointer items-start gap-3 rounded-xl border bg-white px-4 py-3 transition-colors';
  const idle =
    accent === 'teal'
      ? 'border-slate-200 hover:border-teal-300 hover:bg-teal-50/40'
      : 'border-slate-200 hover:border-pink-300 hover:bg-pink-50/40';
  const active = accent === 'teal' ? 'border-teal-500 bg-teal-50/50' : 'border-pink-500 bg-pink-50/50';
  return `${base} ${selected ? active : idle}`;
};

const LegacyMainReason: React.FC<
  Pick<AcademicRenewalFormFieldsProps, 'formData' | 'errors' | 't' | 'handleInputChange' | 'accent'>
> = ({ formData, errors, t, handleInputChange, accent = 'pink' }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-3">
      {t.questionMainReason} <span className="text-red-500">*</span>
    </label>
    <div className="space-y-2">
      {t.mainReasonOptions.map(option => (
        <label key={option.value} className="flex items-center cursor-pointer">
          <input
            type="radio"
            name="mainReason"
            value={option.value}
            checked={formData.mainReason === option.value}
            onChange={e => handleInputChange('mainReason', e.target.value)}
            className={`h-4 w-4 ${accentRadio[accent]}`}
          />
          <span className="ml-2 text-sm text-gray-700">{option.label}</span>
        </label>
      ))}
    </div>
    {errors.mainReason && <p className="mt-1 text-sm text-red-600">{errors.mainReason}</p>}
  </div>
);

const LegacyUsageFrequency: React.FC<
  Pick<
    AcademicRenewalFormFieldsProps,
    'formData' | 'errors' | 't' | 'handleInputChange' | 'setMultiSelect' | 'accent'
  >
> = ({ formData, errors, t, handleInputChange, setMultiSelect, accent = 'pink' }) => (
  <div>
    <MultiSelectDropdown
      label={t.questionUsageFrequency}
      options={t.usageFrequencyOptions}
      value={formData.usageFrequency ?? []}
      onChange={v => setMultiSelect('usageFrequency', v)}
      placeholder={t.selectFrequencyPlaceholder}
      theme={multiTheme(accent)}
      error={errors.usageFrequency}
      required
    />
    {formData.usageFrequency?.includes('other') && (
      <input
        type="text"
        value={formData.applicationTypeOther ?? ''}
        onChange={e => handleInputChange('applicationTypeOther', e.target.value)}
        placeholder={t.otherSpecifyPlaceholder}
        className={`mt-2 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none sm:text-sm ${accentInput[accent]}`}
      />
    )}
    {errors.applicationTypeOther && (
      <p className="mt-1 text-sm text-red-600">{errors.applicationTypeOther}</p>
    )}
  </div>
);

const LegacyAccess: React.FC<
  Pick<AcademicRenewalFormFieldsProps, 'formData' | 'errors' | 't' | 'setMultiSelect' | 'accent'>
> = ({ formData, errors, t, setMultiSelect, accent = 'pink' }) => (
  <div>
    <MultiSelectDropdown
      label={t.questionAccess}
      options={t.accessOptions}
      value={formData.access ?? []}
      onChange={v => setMultiSelect('access', v)}
      placeholder={t.selectAccessPlaceholder}
      theme={multiTheme(accent)}
      error={errors.access}
      required
    />
  </div>
);

const LegacyUsefulness: React.FC<
  Pick<AcademicRenewalFormFieldsProps, 'formData' | 'errors' | 't' | 'handleInputChange' | 'accent'>
> = ({ formData, errors, t, handleInputChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-3">
      {t.questionUsefulness} <span className="text-red-500">*</span>
    </label>
    <div className="space-y-2">
      <input
        type="range"
        min="1"
        max="5"
        value={formData.usefulness}
        onChange={e => handleInputChange('usefulness', parseInt(e.target.value, 10))}
        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
      />
      <div className="flex justify-between text-xs text-gray-500">
        <span>1</span>
        <span>2</span>
        <span>3</span>
        <span>4</span>
        <span>5</span>
      </div>
      <div className="text-center">
        <span className="text-sm font-medium text-gray-700">
          {t.usefulnessLabels[formData.usefulness - 1]}
        </span>
      </div>
    </div>
    {errors.usefulness && <p className="mt-1 text-sm text-red-600">{errors.usefulness}</p>}
  </div>
);

const LegacySuggestions: React.FC<
  Pick<AcademicRenewalFormFieldsProps, 'formData' | 't' | 'handleInputChange' | 'accent'>
> = ({ formData, t, handleInputChange, accent = 'pink' }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-3">{t.questionSuggestions}</label>
    <textarea
      value={formData.suggestions}
      onChange={e => handleInputChange('suggestions', e.target.value)}
      rows={4}
      className={`block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none sm:text-sm ${accentInput[accent]}`}
      placeholder={t.suggestionsPlaceholder}
    />
  </div>
);

const LegacyMarketing: React.FC<
  Pick<AcademicRenewalFormFieldsProps, 'formData' | 'errors' | 't' | 'handleInputChange' | 'accent'>
> = ({ formData, errors, t, handleInputChange, accent = 'pink' }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-3">
      {t.questionMarketingConsent} <span className="text-red-500">*</span>
    </label>
    <div className="space-y-2">
      <label className="flex items-center cursor-pointer">
        <input
          type="radio"
          name="marketingConsent"
          value="yes"
          checked={formData.marketingConsent === 'yes'}
          onChange={() => handleInputChange('marketingConsent', 'yes')}
          className={`h-4 w-4 ${accentRadio[accent]}`}
        />
        <span className="ml-2 text-sm text-gray-700">{t.marketingConsentYes}</span>
      </label>
      <label className="flex items-center cursor-pointer">
        <input
          type="radio"
          name="marketingConsent"
          value="no"
          checked={formData.marketingConsent === 'no'}
          onChange={() => handleInputChange('marketingConsent', 'no')}
          className={`h-4 w-4 ${accentRadio[accent]}`}
        />
        <span className="ml-2 text-sm text-gray-700">{t.marketingConsentNo}</span>
      </label>
    </div>
    {errors.marketingConsent && <p className="mt-1 text-sm text-red-600">{errors.marketingConsent}</p>}
  </div>
);

const StepMainReason: React.FC<
  Pick<AcademicRenewalFormFieldsProps, 'formData' | 'errors' | 't' | 'handleInputChange' | 'accent'>
> = ({ formData, errors, t, handleInputChange, accent = 'pink' }) => (
  <div>
    <p className="text-base font-medium text-slate-900">
      {t.questionMainReason} <span className="text-red-500">*</span>
    </p>
    <p className="mt-1 text-sm text-slate-500">{t.helpSelectOne}</p>
    <ul className="mt-6 space-y-3">
      {t.mainReasonOptions.map(option => {
        const selected = formData.mainReason === option.value;
        return (
          <li key={option.value}>
            <label className={optionRowClass(accent, selected)}>
              <input
                type="radio"
                name="mainReason"
                value={option.value}
                checked={selected}
                onChange={e => handleInputChange('mainReason', e.target.value)}
                className={`mt-0.5 h-4 w-4 shrink-0 ${accentRadio[accent]}`}
              />
              <span className="text-sm leading-snug text-slate-700">{option.label}</span>
            </label>
          </li>
        );
      })}
    </ul>
    {errors.mainReason && <p className="mt-1 text-sm text-red-600">{errors.mainReason}</p>}
  </div>
);

const StepUsageFrequency: React.FC<
  Pick<
    AcademicRenewalFormFieldsProps,
    'formData' | 'errors' | 't' | 'handleInputChange' | 'setMultiSelect' | 'accent'
  >
> = ({ formData, errors, t, handleInputChange, setMultiSelect, accent = 'pink' }) => {
  const selected = formData.usageFrequency ?? [];
  const toggle = (value: string) => {
    const next = selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value];
    setMultiSelect('usageFrequency', next);
  };

  return (
    <div>
      <p className="text-base font-medium text-slate-900">
        {t.questionUsageFrequency} <span className="text-red-500">*</span>
      </p>
      <p className="mt-1 text-sm text-slate-500">{t.helpSelectAll}</p>
      <ul className="mt-6 space-y-3">
        {t.usageFrequencyOptions.map(opt => {
          const isOn = selected.includes(opt.value);
          return (
            <li key={opt.value}>
              <label className={optionRowClass(accent, isOn)}>
                <input
                  type="checkbox"
                  checked={isOn}
                  onChange={() => toggle(opt.value)}
                  className={`mt-0.5 h-4 w-4 shrink-0 rounded ${accentCheckbox[accent]}`}
                />
                <span className="text-sm leading-snug text-slate-700">{opt.label}</span>
              </label>
            </li>
          );
        })}
      </ul>
      {errors.usageFrequency && <p className="mt-1 text-sm text-red-600">{errors.usageFrequency}</p>}
      {formData.usageFrequency?.includes('other') && (
        <input
          type="text"
          value={formData.applicationTypeOther ?? ''}
          onChange={e => handleInputChange('applicationTypeOther', e.target.value)}
          placeholder={t.otherSpecifyPlaceholder}
          className={`mt-4 block w-full rounded-xl border border-slate-200 px-4 py-3 text-sm shadow-sm focus:outline-none ${accentInput[accent]}`}
        />
      )}
      {errors.applicationTypeOther && (
        <p className="mt-1 text-sm text-red-600">{errors.applicationTypeOther}</p>
      )}
    </div>
  );
};

const StepAccess: React.FC<
  Pick<AcademicRenewalFormFieldsProps, 'formData' | 'errors' | 't' | 'setMultiSelect' | 'accent'>
> = ({ formData, errors, t, setMultiSelect, accent = 'pink' }) => {
  const selected = formData.access ?? [];
  const toggle = (value: string) => {
    const next = selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value];
    setMultiSelect('access', next);
  };

  return (
    <div>
      <p className="text-base font-medium text-slate-900">
        {t.questionAccess} <span className="text-red-500">*</span>
      </p>
      <p className="mt-1 text-sm text-slate-500">{t.helpSelectAll}</p>
      <ul className="mt-6 space-y-3">
        {t.accessOptions.map(opt => {
          const isOn = selected.includes(opt.value);
          return (
            <li key={opt.value}>
              <label className={optionRowClass(accent, isOn)}>
                <input
                  type="checkbox"
                  checked={isOn}
                  onChange={() => toggle(opt.value)}
                  className={`mt-0.5 h-4 w-4 shrink-0 rounded ${accentCheckbox[accent]}`}
                />
                <span className="text-sm leading-snug text-slate-700">{opt.label}</span>
              </label>
            </li>
          );
        })}
      </ul>
      {errors.access && <p className="mt-1 text-sm text-red-600">{errors.access}</p>}
    </div>
  );
};

const StepUsefulness: React.FC<
  Pick<AcademicRenewalFormFieldsProps, 'formData' | 'errors' | 't' | 'handleInputChange' | 'accent'>
> = ({ formData, errors, t, handleInputChange, accent = 'pink' }) => (
  <div>
    <p className="text-base font-medium text-slate-900">
      {t.questionUsefulness} <span className="text-red-500">*</span>
    </p>
    <p className="mt-1 text-sm text-slate-500">{t.helpSelectOne}</p>
    <ul className="mt-6 space-y-3">
      {t.usefulnessLabels.map((label, i) => {
        const value = i + 1;
        const selected = formData.usefulness === value;
        return (
          <li key={value}>
            <label className={optionRowClass(accent, selected)}>
              <input
                type="radio"
                name="usefulness"
                value={value}
                checked={selected}
                onChange={() => handleInputChange('usefulness', value)}
                className={`mt-0.5 h-4 w-4 shrink-0 ${accentRadio[accent]}`}
              />
              <span className="text-sm leading-snug text-slate-700">
                <span className="font-medium text-slate-900">{value}.</span> {label}
              </span>
            </label>
          </li>
        );
      })}
    </ul>
    {errors.usefulness && <p className="mt-1 text-sm text-red-600">{errors.usefulness}</p>}
  </div>
);

const StepSuggestions: React.FC<
  Pick<AcademicRenewalFormFieldsProps, 'formData' | 't' | 'handleInputChange' | 'accent'>
> = ({ formData, t, handleInputChange, accent = 'pink' }) => (
  <div>
    <p className="text-base font-medium text-slate-900">{t.questionSuggestions}</p>
    <textarea
      value={formData.suggestions}
      onChange={e => handleInputChange('suggestions', e.target.value)}
      rows={4}
      className={`mt-4 block w-full rounded-xl border border-slate-200 px-4 py-3 text-sm shadow-sm focus:outline-none ${accentInput[accent]}`}
      placeholder={t.suggestionsPlaceholder}
    />
  </div>
);

const StepMarketing: React.FC<
  Pick<AcademicRenewalFormFieldsProps, 'formData' | 'errors' | 't' | 'handleInputChange' | 'accent'>
> = ({ formData, errors, t, handleInputChange, accent = 'pink' }) => (
  <div>
    <p className="text-base font-medium text-slate-900">
      {t.questionMarketingConsent} <span className="text-red-500">*</span>
    </p>
    <p className="mt-1 text-sm text-slate-500">{t.helpSelectOne}</p>
    <ul className="mt-6 space-y-3">
      <li>
        <label className={optionRowClass(accent, formData.marketingConsent === 'yes')}>
          <input
            type="radio"
            name="marketingConsent"
            value="yes"
            checked={formData.marketingConsent === 'yes'}
            onChange={() => handleInputChange('marketingConsent', 'yes')}
            className={`mt-0.5 h-4 w-4 shrink-0 ${accentRadio[accent]}`}
          />
          <span className="text-sm leading-snug text-slate-700">{t.marketingConsentYes}</span>
        </label>
      </li>
      <li>
        <label className={optionRowClass(accent, formData.marketingConsent === 'no')}>
          <input
            type="radio"
            name="marketingConsent"
            value="no"
            checked={formData.marketingConsent === 'no'}
            onChange={() => handleInputChange('marketingConsent', 'no')}
            className={`mt-0.5 h-4 w-4 shrink-0 ${accentRadio[accent]}`}
          />
          <span className="text-sm leading-snug text-slate-700">{t.marketingConsentNo}</span>
        </label>
      </li>
    </ul>
    {errors.marketingConsent && <p className="mt-1 text-sm text-red-600">{errors.marketingConsent}</p>}
  </div>
);

const AcademicRenewalFormFields: React.FC<AcademicRenewalFormFieldsProps> = ({
  mode,
  stepIndex = 0,
  formData,
  errors,
  t,
  accent = 'pink',
  handleInputChange,
  setMultiSelect,
}) => {
  if (mode === 'full') {
    return (
      <div className="space-y-6">
        <LegacyMainReason
          formData={formData}
          errors={errors}
          t={t}
          handleInputChange={handleInputChange}
          accent={accent}
        />
        <LegacyUsageFrequency
          formData={formData}
          errors={errors}
          t={t}
          handleInputChange={handleInputChange}
          setMultiSelect={setMultiSelect}
          accent={accent}
        />
        <LegacyAccess
          formData={formData}
          errors={errors}
          t={t}
          setMultiSelect={setMultiSelect}
          accent={accent}
        />
        <LegacyUsefulness
          formData={formData}
          errors={errors}
          t={t}
          handleInputChange={handleInputChange}
          accent={accent}
        />
        <LegacySuggestions formData={formData} t={t} handleInputChange={handleInputChange} accent={accent} />
        {formData.suggestions?.trim() && (
          <LegacyMarketing
            formData={formData}
            errors={errors}
            t={t}
            handleInputChange={handleInputChange}
            accent={accent}
          />
        )}
      </div>
    );
  }

  switch (stepIndex) {
    case 0:
      return (
        <StepMainReason
          formData={formData}
          errors={errors}
          t={t}
          handleInputChange={handleInputChange}
          accent={accent}
        />
      );
    case 1:
      return (
        <StepUsageFrequency
          formData={formData}
          errors={errors}
          t={t}
          handleInputChange={handleInputChange}
          setMultiSelect={setMultiSelect}
          accent={accent}
        />
      );
    case 2:
      return (
        <StepAccess
          formData={formData}
          errors={errors}
          t={t}
          setMultiSelect={setMultiSelect}
          accent={accent}
        />
      );
    case 3:
      return (
        <StepUsefulness
          formData={formData}
          errors={errors}
          t={t}
          handleInputChange={handleInputChange}
          accent={accent}
        />
      );
    case 4:
      return (
        <StepSuggestions formData={formData} t={t} handleInputChange={handleInputChange} accent={accent} />
      );
    case 5:
      return (
        <StepMarketing
          formData={formData}
          errors={errors}
          t={t}
          handleInputChange={handleInputChange}
          accent={accent}
        />
      );
    default:
      return null;
  }
};

export default AcademicRenewalFormFields;
